import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import {nftButtonSx} from '@/styles/nftButtonSx';
import Image from 'next/image';
import parse from 'html-react-parser';
import {solutionsData} from '@/util/contents/blockchain-data';
import {
    contentMaxWidth,
    colors,
    fontSize,
    fontWeight,
    lineHeight,
    spacing,
    gradients,
    overlay,
    borderRadius,
    borderWidth,
    transition,
    typography
} from '@/styles/tokens';
import Eyebrow from '@/components/Common/Eyebrow';
import {scrollToElement} from '@/util/motion/smoothScroll';

// ─── Module-level sx constants ────────────────────────────────────────────────

const accordionGridSx = {
    display: 'flex',
    flexDirection: 'column'
} as const;

const accordionRowBoxSx = {mb: 0} as const;

const accordionHeaderItemSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: 3,
    cursor: 'pointer',
    borderBottom: `1px solid ${colors.neutral.borderSubtle}`,
    transition: `all ${transition.slow}`
} as const;

const accordionTitleSx = {
    fontWeight: fontWeight.bold,
    fontSize: fontSize[20],
    color: colors.text.primary,
    '& a': {
        color: 'inherit',
        textDecoration: 'none',
        pointerEvents: 'none',
        cursor: 'default'
    }
} as const;

// One expansion per item, rendered once (as in plain-sections/SolutionsAccordion). The row reproduces
// <Grid container columnSpacing={4}> and each header <Grid item xs={12} md={6}>. In DOM order an expansion
// follows its own header (the phone layout); from md, `order` moves the row's expansions below both headers
// (the desktop layout), which used to be a second, desktop-only copy.
const rowGridSx = {
    boxSizing: 'border-box',
    display: 'flex',
    flexFlow: 'row wrap',
    width: 'calc(100% + 32px)',
    ml: '-32px'
} as const;

const rowItemSx = {
    boxSizing: 'border-box',
    m: 0,
    flexBasis: {xs: '100%', md: '50%'},
    flexGrow: 0,
    maxWidth: {xs: '100%', md: '50%'},
    pl: '32px'
} as const;

const getExpansionSx = (isExpanded: boolean, isFirst: boolean, isLast: boolean) =>
    ({
        boxSizing: 'border-box',
        flexBasis: '100%',
        maxWidth: {xs: 'calc(100% - 32px)', md: '100%'},
        ml: {xs: '32px', md: 0},
        pl: {xs: 0, md: '32px'},
        order: {xs: 0, md: 1},
        // The desktop block had 16px above and below the row's expansions, even when all were collapsed.
        mt: {xs: 0, md: isFirst ? spacing[16] : 0},
        mb: {xs: 0, md: isLast ? spacing[16] : 0},
        borderBottom: isExpanded ? {xs: `1px solid ${colors.neutral.borderSubtle}`, md: 'none'} : 'none'
    } as const);

const expandedCardBoxSx = {
    position: 'relative',
    background: gradients.darkSection,
    borderRadius: borderRadius.xl,
    p: {xs: spacing[32], md: spacing[48]},
    color: colors.text.inverse,
    mt: {xs: spacing[8], md: 0},
    mb: {xs: spacing[16], md: 0},
    '& a': {
        color: 'inherit',
        textDecoration: 'underline',
        '&:hover': {
            opacity: 0.8
        }
    }
} as const;

const closeIconButtonSx = {
    position: 'absolute',
    top: {xs: spacing[8], md: spacing[16]},
    right: {xs: spacing[8], md: spacing[16]},
    color: overlay.whiteSoft
} as const;

const expandedCardTitleSx = {
    fontWeight: fontWeight.bold,
    fontSize: {xs: fontSize[24], md: fontSize[32]},
    mb: spacing[16]
} as const;

const expandedCardDescSx = {
    fontSize: fontSize[16],
    lineHeight: lineHeight[28],
    color: colors.text.inverse,
    mb: spacing[32]
} as const;

const bulletListSx = {listStyleType: 'none', p: 0, m: 0, mb: spacing[32]} as const;

const bulletListItemSx = {
    display: 'flex',
    mb: spacing[12],
    alignItems: 'flex-start'
} as const;

const bulletIconSx = {
    width: spacing[8],
    height: spacing[8],
    minWidth: spacing[8],
    flexShrink: 0,
    bgcolor: colors.text.inverse,
    mt: spacing[8],
    mr: spacing[16]
} as const;

const bulletTextSx = {
    fontSize: fontSize[16],
    lineHeight: lineHeight[24],
    color: colors.neutral.grayLight
} as const;

const cardFooterTextSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.semibold,
    color: colors.text.inverse,
    mb: spacing[32]
} as const;

const cardActionBoxSx = {
    display: 'flex',
    gap: spacing[16],
    flexDirection: {xs: 'column', sm: 'row'},
    alignItems: {xs: 'flex-start', sm: 'center'}
} as const;

const btnSecondarySx = {
    borderColor: colors.text.inverse,
    color: colors.text.inverse,
    borderWidth: borderWidth.thin,
    borderStyle: 'solid',
    backgroundColor: 'transparent',
    textTransform: 'none !important',
    borderRadius: `${borderRadius.md} !important`,
    '&:hover': {
        borderColor: `${colors.text.inverse} !important`,
        backgroundColor: `${colors.background.default} !important`,
        color: `${colors.text.primary} !important`
    }
} as const;

const imageWrapperSx = {
    width: '100%',
    height: {xs: 200, md: 350},
    position: 'relative',
    borderRadius: borderRadius.lg
} as const;

const sectionHeaderSx = {
    textAlign: 'center',
    maxWidth: contentMaxWidth.standard,
    margin: '0 auto',
    mb: spacing[64]
} as const;

const headingTypoSx = {
    ...typography.sectionHeading,
    mb: spacing[24]
} as const;

// ─── Main Component ──────────────────────────────────────────

const SolutionsAccordion = ({eyebrow, heading, subheading, solutionsData: customSolutionsData}: any) => {
    const [expandedIndex, setExpandedIndex] = useState<number>(0);

    const dataToUse = customSolutionsData || solutionsData;

    const toggleAccordion = (index: number, event: React.MouseEvent<HTMLElement>) => {
        const isOpening = expandedIndex !== index;
        setExpandedIndex(isOpening ? index : -1);

        if (isOpening) {
            const target = event.currentTarget;
            setTimeout(() => {
                scrollToElement(target, 100);
            }, 300);
        }
    };

    const closeAccordion = () => {
        setExpandedIndex(-1);
    };

    const rows = [];
    for (let i = 0; i < dataToUse.length; i += 2) {
        rows.push([dataToUse[i], dataToUse[i + 1]].filter(Boolean));
    }

    return (
        <Box className="bg-blue-background top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
            <MUIContainer>
                <Box sx={sectionHeaderSx}>
                    {eyebrow && <Eyebrow text={eyebrow} sx={{textAlign: 'center'}} />}
                    <Typography variant="h1" component="h2" className="font-headers" sx={headingTypoSx}>
                        {heading}
                    </Typography>
                    <Typography variant="subtitle1" component="p" className="font-regular-text remove-underline">
                        {subheading ? parse(subheading) : ''}
                    </Typography>
                </Box>
                <Box sx={accordionGridSx}>
                    {rows?.map((rowItems, rowIndex) => {
                        const startIndex = rowIndex * 2;

                        return (
                            <Box key={rowIndex} sx={accordionRowBoxSx}>
                                {/* Collapse keeps collapsed content mounted, so every item's <h3> title, description,
                                    bullets and footer stay in the server HTML for crawlers (SEO #33), once. */}
                                <Box sx={rowGridSx}>
                                    {rowItems?.map((item, localIdx) => {
                                        const globalIdx = startIndex + localIdx;
                                        const isExpanded = expandedIndex === globalIdx;
                                        return (
                                            <React.Fragment key={globalIdx}>
                                                <Box sx={rowItemSx}>
                                                    <Box
                                                        onClick={(e) => toggleAccordion(globalIdx, e)}
                                                        sx={{
                                                            ...accordionHeaderItemSx,
                                                            borderTop: `1px solid ${colors.neutral.borderSubtle}`
                                                        }}
                                                    >
                                                        <Typography className="font-headers" sx={accordionTitleSx}>
                                                            {item.title ? parse(item.title) : ''}
                                                        </Typography>
                                                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                    </Box>
                                                </Box>
                                                <Box sx={getExpansionSx(isExpanded, localIdx === 0, localIdx === rowItems.length - 1)}>
                                                    <Collapse in={isExpanded} timeout="auto">
                                                        <ExpandedCardContent item={item} onClose={closeAccordion} />
                                                    </Collapse>
                                                </Box>
                                            </React.Fragment>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </MUIContainer>
        </Box>
    );
};

const ExpandedCardContent = ({item, onClose, titleComponent = 'h3'}: {item: any; onClose: () => void; titleComponent?: any}) => {
    return (
        <Box sx={expandedCardBoxSx}>
            <IconButton onClick={onClose} sx={closeIconButtonSx} aria-label="close">
                <CloseIcon />
            </IconButton>

            <Grid container spacing={{xs: 3, md: 5}} alignItems="center">
                <Grid item xs={12} md={7}>
                    <Typography variant="h3" component={titleComponent} sx={expandedCardTitleSx}>
                        {item.title ? parse(item.title) : ''}
                    </Typography>
                    <Typography className="solution-link" component="p" sx={expandedCardDescSx}>
                        {item.description ? parse(item.description) : ''}
                    </Typography>

                    <Box className="solution-link" component="ul" sx={bulletListSx}>
                        {item.points?.map((point: string, idx: number) => (
                            <Box component="li" key={idx} sx={bulletListItemSx}>
                                <Box sx={bulletIconSx} />
                                <Typography component="span" sx={bulletTextSx}>
                                    {parse(point)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Typography component="p" sx={cardFooterTextSx}>
                        {item.footer ? parse(item.footer) : ''}
                    </Typography>

                    <Box sx={cardActionBoxSx}>
                        {item.viewCaseStudyLink && (
                            <MUIButton
                                label={item.viewCaseStudyLabel || 'Case Studies'}
                                aria-label={item.viewCaseStudyLabel || 'Case Studies'}
                                variant="contained"
                                size="medium"
                                disableRipple={true}
                                className="animated-button moving-icon-button"
                                sx={nftButtonSx}
                                onClick={() => globalThis.open(item.viewCaseStudyLink, '_blank')}
                            />
                        )}
                        {item.useCaseLink && (
                            <MUIButton
                                label={item.useCaseLinkLabel || 'Explore Use Cases'}
                                aria-label={item.useCaseLinkLabel || 'Explore Use Cases'}
                                variant="outlined"
                                sx={btnSecondarySx}
                                disableRipple={true}
                                className="moving-icon-button transparent-button"
                                onClick={() => globalThis.open(item.useCaseLink, '_blank')}
                            />
                        )}
                    </Box>
                </Grid>

                <Grid item xs={12} md={5}>
                    <Box sx={imageWrapperSx}>
                        <Image src={item.image} alt={item.title} fill style={{objectFit: 'contain'}} />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default memo(SolutionsAccordion);
